import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    Box,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Collapse,
    CircularProgress,
    Alert,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import {
    Close,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import {
    stockService,
    StockReservation,
} from "../../services/stockService";
import { orderService } from "../../services/orderService";
import { Order } from "../../types";
import { formatDateShort } from "../../utils/date";
import { formatCurrencyBDT } from "../../utils/currency";

interface Props {
    open: boolean;
    onClose: () => void;
    productId: string | null;
    productName?: string;
    reservedQuantity: number;
}

function getStatusColor(
    status: string
): "warning" | "info" | "primary" | "success" | "error" | "default" {
    switch (status) {
        case "PENDING":
            return "warning";
        case "CONFIRMED":
            return "info";
        case "SHIPPED":
            return "primary";
        case "DELIVERED":
            return "success";
        case "CANCELLED":
            return "error";
        default:
            return "default";
    }
}

function getPaymentStatusColor(
    status: string
): "success" | "warning" | "error" | "default" {
    switch (status) {
        case "PAID":
        case "OVERPAID":
            return "success";
        case "PARTIAL":
            return "warning";
        case "UNPAID":
            return "error";
        default:
            return "default";
    }
}

export const StockReservationsDialog: React.FC<Props> = ({
    open,
    onClose,
    productId,
    productName,
    reservedQuantity,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [reservations, setReservations] = useState<StockReservation[]>([]);

    const [expandedOrders, setExpandedOrders] = useState<Set<string>>(
        new Set()
    );
    const [orderDetails, setOrderDetails] = useState<Record<string, Order>>(
        {}
    );
    const [detailsLoading, setDetailsLoading] = useState<
        Record<string, boolean>
    >({});
    const [detailsError, setDetailsError] = useState<Record<string, string>>(
        {}
    );

    useEffect(() => {
        if (!open || !productId) return;
        setExpandedOrders(new Set());
        setOrderDetails({});
        setDetailsLoading({});
        setDetailsError({});
        setLoading(true);
        setError("");
        stockService
            .getStockReservations(productId)
            .then(setReservations)
            .catch(() => setError("Failed to load reservations"))
            .finally(() => setLoading(false));
    }, [open, productId]);

    const handleToggleOrder = (orderId: string) => {
        setExpandedOrders((prev) => {
            const next = new Set(prev);
            if (next.has(orderId)) {
                next.delete(orderId);
            } else {
                next.add(orderId);
            }
            return next;
        });
        if (!orderDetails[orderId] && !detailsLoading[orderId]) {
            setDetailsLoading((prev) => ({ ...prev, [orderId]: true }));
            orderService
                .getOrderById(orderId)
                .then((data) => {
                    setOrderDetails((prev) => ({ ...prev, [orderId]: data }));
                })
                .catch(() => {
                    setDetailsError((prev) => ({
                        ...prev,
                        [orderId]: "Failed to load order details",
                    }));
                })
                .finally(() => {
                    setDetailsLoading((prev) => ({
                        ...prev,
                        [orderId]: false,
                    }));
                });
        }
    };

    function renderOrderDetails(orderId: string) {
        if (detailsLoading[orderId]) {
            return (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={20} />
                </Box>
            );
        }
        if (detailsError[orderId]) {
            return (
                <Alert severity="error" sx={{ my: 1 }}>
                    {detailsError[orderId]}
                </Alert>
            );
        }
        const order = orderDetails[orderId];
        if (!order) return null;
        return (
            <Box sx={{ py: 1.5 }}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5, mb: 1.5 }}>
                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                            Order Total
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                            {formatCurrencyBDT(order.total_amount)}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                            Payment
                        </Typography>
                        <Chip
                            label={order.payment_status}
                            color={getPaymentStatusColor(order.payment_status)}
                            size="small"
                            variant="outlined"
                        />
                    </Box>
                    {order.shipping_address && (
                        <Box sx={{ flex: 1, minWidth: 180 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Ship to
                            </Typography>
                            <Typography variant="body2">
                                {order.shipping_address}
                            </Typography>
                        </Box>
                    )}
                </Box>
                {order.items && order.items.length > 0 && (
                    <Box>
                        {order.items.map((item) => (
                            <Box
                                key={item.item_id}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 1,
                                    py: 0.5,
                                    "&:not(:last-child)": {
                                        borderBottom: "1px solid",
                                        borderColor: "divider",
                                    },
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    noWrap
                                    sx={{ flex: 1, minWidth: 0 }}
                                >
                                    {item.product?.name ?? item.product_id}
                                    {item.is_gift && (
                                        <Chip
                                            label="Gift"
                                            size="small"
                                            variant="outlined"
                                            sx={{ ml: 0.75, height: 16, fontSize: "0.6rem" }}
                                        />
                                    )}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ flexShrink: 0 }}
                                >
                                    {item.quantity} × {formatCurrencyBDT(item.unit_price)} ={" "}
                                    {formatCurrencyBDT(item.total_price)}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}
                {order.notes && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 1 }}
                    >
                        Notes: {order.notes}
                    </Typography>
                )}
            </Box>
        );
    }

    const accountedQuantity = reservations.reduce(
        (sum, r) => sum + r.quantity,
        0
    );
    const unaccountedQuantity = Math.max(
        reservedQuantity - accountedQuantity,
        0
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            fullScreen={isMobile}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 2,
                }}
            >
                <Box>
                    <Typography variant="h6" component="span">
                        Reserved Stock
                    </Typography>
                    {productName && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.25 }}
                        >
                            {productName}
                        </Typography>
                    )}
                </Box>
                <IconButton size="small" onClick={onClose}>
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : error ? (
                    <Alert severity="error">{error}</Alert>
                ) : reservations.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No open orders are currently holding a reservation for this
                        product.
                    </Typography>
                ) : (
                    <>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Order</TableCell>
                                    <TableCell>Customer</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell align="right">Qty</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {reservations.map((r) => {
                                    const isExpanded = expandedOrders.has(
                                        r.order_id
                                    );
                                    return (
                                        <React.Fragment key={r.order_id}>
                                            <TableRow hover>
                                                <TableCell>
                                                    <Box
                                                        onClick={() =>
                                                            handleToggleOrder(
                                                                r.order_id
                                                            )
                                                        }
                                                        sx={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            gap: 0.25,
                                                            cursor: "pointer",
                                                            color: "primary.main",
                                                            "&:hover": {
                                                                textDecoration:
                                                                    "underline",
                                                            },
                                                        }}
                                                    >
                                                        {isExpanded ? (
                                                            <ExpandLessIcon fontSize="small" />
                                                        ) : (
                                                            <ExpandMoreIcon fontSize="small" />
                                                        )}
                                                        {r.order_number}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    {r.customer_name || (
                                                        <em style={{ opacity: 0.5 }}>—</em>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={r.status}
                                                        color={getStatusColor(r.status)}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {formatDateShort(r.order_date)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {r.quantity}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell
                                                    colSpan={5}
                                                    sx={{
                                                        py: 0,
                                                        borderBottom: isExpanded
                                                            ? undefined
                                                            : "none",
                                                    }}
                                                >
                                                    <Collapse
                                                        in={isExpanded}
                                                        timeout="auto"
                                                        unmountOnExit
                                                    >
                                                        {renderOrderDetails(r.order_id)}
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>
                        {unaccountedQuantity > 0 && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                sx={{ mt: 2 }}
                            >
                                {unaccountedQuantity} additional unit
                                {unaccountedQuantity === 1 ? "" : "s"} reserved outside
                                of tracked orders (e.g. manual holds).
                            </Typography>
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};
